import React, { memo } from 'react';
import { ConnectionType } from '../types';

interface ConnectionPreviewProps {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  isValid: boolean;
  connectionType?: ConnectionType;
  sourceNodeId?: string;
  targetNodeId?: string;
}

const ConnectionPreview: React.FC<ConnectionPreviewProps> = ({
  x1,
  y1,
  x2,
  y2,
  isValid,
  connectionType,
  sourceNodeId,
  targetNodeId
}) => {
  // Calculate midpoint for label
  const midX = (x1 + x2) / 2;
  const midY = (y1 + y2) / 2;
  
  // Calculate angle for arrow
  const angle = Math.atan2(y2 - y1, x2 - x1) * (180 / Math.PI);
  
  // Arrow head points
  const arrowLength = 12;
  const arrowWidth = 8;
  const arrowAngle = 30;
  
  const arrowX = x2 - arrowLength * Math.cos(angle * Math.PI / 180);
  const arrowY = y2 - arrowLength * Math.sin(angle * Math.PI / 180);
  
  const arrowPoint1X = arrowX - arrowWidth * Math.cos((angle + arrowAngle) * Math.PI / 180);
  const arrowPoint1Y = arrowY - arrowWidth * Math.sin((angle + arrowAngle) * Math.PI / 180);
  
  const arrowPoint2X = arrowX - arrowWidth * Math.cos((angle - arrowAngle) * Math.PI / 180);
  const arrowPoint2Y = arrowY - arrowWidth * Math.sin((angle - arrowAngle) * Math.PI / 180);

  const strokeColor = isValid ? '#3b82f6' : '#ef4444';
  const strokeWidth = isValid ? 2.5 : 2;

  return (
    <svg 
      className="absolute inset-0 pointer-events-none z-[100]" 
      style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}
    >
      {/* Animated dash pattern */}
      <defs>
        <style>
          {`
            .connection-line {
              stroke-dasharray: ${isValid ? "8,4" : "4,4"};
              animation: ${isValid ? "dashdraw 0.5s linear infinite" : "none"};
              transition: stroke 0.2s ease, stroke-width 0.2s ease;
            }
            @keyframes dashdraw {
              to {
                stroke-dashoffset: -12;
              }
            }
            .connection-arrow {
              animation: pulse 2s ease-in-out infinite;
            }
          `}
        </style>
      </defs>
      <line 
        x1={x1} 
        y1={y1} 
        x2={x2} 
        y2={y2} 
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        className="connection-line"
        opacity={0.8}
        markerEnd="none"
      />
      
      {/* Arrow head with pulse animation */}
      {isValid && (
        <polygon
          points={`${x2},${y2} ${arrowPoint1X},${arrowPoint1Y} ${arrowPoint2X},${arrowPoint2Y}`}
          fill={strokeColor}
          opacity={0.8}
          className="connection-arrow"
        />
      )}
      
      {/* Invalid indicator (X) */}
      {!isValid && (
        <>
          <circle
            cx={midX}
            cy={midY}
            r="12"
            fill="white"
            stroke={strokeColor}
            strokeWidth="2"
            opacity={0.9}
          />
          <line
            x1={midX - 6}
            y1={midY - 6}
            x2={midX + 6}
            y2={midY + 6}
            stroke={strokeColor}
            strokeWidth="2"
          />
          <line
            x1={midX + 6}
            y1={midY - 6}
            x2={midX - 6}
            y2={midY + 6}
            stroke={strokeColor}
            strokeWidth="2"
          />
        </>
      )}
      
      {/* Connection type label with fade-in */}
      {isValid && connectionType && (
        <g className="animate-fade-in" style={{ animationDuration: '200ms' }}>
          <rect
            x={midX - 30}
            y={midY - 10}
            width="60"
            height="20"
            rx="4"
            fill="white"
            stroke={strokeColor}
            strokeWidth="1.5"
            opacity={0.95}
            style={{ transition: 'opacity 0.2s ease' }}
          />
          <text
            x={midX}
            y={midY + 4}
            textAnchor="middle"
            fontSize="10"
            fontWeight="600"
            fill={strokeColor}
            style={{ transition: 'opacity 0.2s ease' }}
          >
            {connectionType}
          </text>
        </g>
      )}
      
      {/* Warning indicator for invalid connections */}
      {!isValid && (
        <g>
          <circle
            cx={midX}
            cy={midY}
            r="15"
            fill="white"
            stroke={strokeColor}
            strokeWidth="2"
            opacity={0.9}
          />
          <text
            x={midX}
            y={midY + 5}
            textAnchor="middle"
            fontSize="16"
            fontWeight="bold"
            fill={strokeColor}
          >
            ⚠
          </text>
        </g>
      )}
    </svg>
  );
};

export default memo(ConnectionPreview);
